import { Clock, DoorOpen, LayoutList, X } from 'lucide-react';
import type { TipoSlotHorario, Turno } from '../../types/models';
import {
  DIAS_OPTIONS,
  type TemplateSlot,
  turnoAccent,
  type DEFAULT_FORM,
} from '../../pages/admin/turnos-shared';
import { TurnoEditSidePanel } from './TurnoEditSidePanel';

export type TurnoFormState = typeof DEFAULT_FORM;

type Props = {
  form: TurnoFormState;
  setForm: React.Dispatch<React.SetStateAction<TurnoFormState>>;
  template: TemplateSlot[];
  editing: Turno | null;
  totalAulas: number;
  totalIntervalos: number;
  onToggleDia: (dia: string) => void;
  onAddSlot: (tipo: TipoSlotHorario) => void;
  onRemoveSlot: (index: number) => void;
  onUpdateSlot: (index: number, field: keyof TemplateSlot, value: string) => void;
};

function HeroBlock({ form, setForm, onToggleDia }: Pick<Props, 'form' | 'setForm' | 'onToggleDia'>) {
  const accent = turnoAccent(form.nome);

  return (
    <>
      <div className="ac-turno-modal__stripe" style={{ background: accent }} aria-hidden />
      <div className="ac-turno-modal__hero">
        <div
          className="ac-turno-modal__hero-jornada"
          style={{ '--turno-accent': accent } as React.CSSProperties}
        >
          <span className="ac-turno-modal__hero-jornada-lbl">Jornada oficial</span>
          <div className="ac-turno-modal__hero-times">
            <div className="ac-turno-modal__hero-time-block">
              <span className="ac-turno-modal__hero-time-val">{form.horaEntradaOficial}</span>
              <span className="ac-turno-modal__hero-time-lbl">Entrada</span>
            </div>
            <span className="ac-turno-modal__hero-arrow" aria-hidden>→</span>
            <div className="ac-turno-modal__hero-time-block">
              <span className="ac-turno-modal__hero-time-val">{form.horaSaidaOficial}</span>
              <span className="ac-turno-modal__hero-time-lbl">Saída</span>
            </div>
          </div>
          <div className="ac-turno-modal__hero-portoes">
            <DoorOpen size={12} />
            Portões {form.horaAberturaPortoes} – {form.horaFechamentoPortoes}
          </div>
          <div className="ac-turno-modal__hero-tol">
            Tolerância <strong>{form.toleranciaAtrasoMin} min</strong>
          </div>
        </div>

        <div className="ac-turno-modal__hero-fields">
          <label className="ac-turno-field">
            <span className="ac-turno-field__label">Nome do turno *</span>
            <input
              className="ac-turno-field__input ac-turno-field__input--lg"
              value={form.nome}
              onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))}
              placeholder="Ex.: Manhã Fundamental I"
              autoFocus
            />
          </label>
          <div className="ac-turno-field">
            <span className="ac-turno-field__label">Dias de funcionamento</span>
            <div className="ac-turno-modal__dias" role="group" aria-label="Dias da semana">
              {DIAS_OPTIONS.map((d) => (
                <button
                  key={d.value}
                  type="button"
                  className={`ac-turno-modal__dia${form.diasFuncionamento.includes(d.value) ? ' ac-turno-modal__dia--on' : ''}`}
                  onClick={() => onToggleDia(d.value)}
                >
                  {d.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function PortoesSection({ form, setForm }: Pick<Props, 'form' | 'setForm'>) {
  return (
    <section className="ac-turno-section">
      <div className="ac-turno-section__head">
        <Clock size={14} />
        <h3 className="ac-turno-section__title">Horários de portão</h3>
      </div>
      <div className="ac-turno-section__grid ac-turno-section__grid--4">
        <label className="ac-turno-field">
          <span className="ac-turno-field__label">Abertura portões</span>
          <input
            className="ac-turno-field__input ac-turno-field__input--time"
            type="time"
            value={form.horaAberturaPortoes}
            onChange={(e) => setForm((f) => ({ ...f, horaAberturaPortoes: e.target.value }))}
          />
        </label>
        <label className="ac-turno-field">
          <span className="ac-turno-field__label">Entrada oficial</span>
          <input
            className="ac-turno-field__input ac-turno-field__input--time"
            type="time"
            value={form.horaEntradaOficial}
            onChange={(e) => setForm((f) => ({ ...f, horaEntradaOficial: e.target.value }))}
          />
        </label>
        <label className="ac-turno-field">
          <span className="ac-turno-field__label">Saída oficial</span>
          <input
            className="ac-turno-field__input ac-turno-field__input--time"
            type="time"
            value={form.horaSaidaOficial}
            onChange={(e) => setForm((f) => ({ ...f, horaSaidaOficial: e.target.value }))}
          />
        </label>
        <label className="ac-turno-field">
          <span className="ac-turno-field__label">Fechamento portões</span>
          <input
            className="ac-turno-field__input ac-turno-field__input--time"
            type="time"
            value={form.horaFechamentoPortoes}
            onChange={(e) => setForm((f) => ({ ...f, horaFechamentoPortoes: e.target.value }))}
          />
        </label>
      </div>
      <label className="ac-turno-field ac-turno-field--tol">
        <span className="ac-turno-field__label">Tolerância de atraso (minutos)</span>
        <input
          className="ac-turno-field__input ac-turno-field__input--num"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={3}
          value={form.toleranciaAtrasoMin !== undefined && form.toleranciaAtrasoMin !== null ? String(form.toleranciaAtrasoMin) : ''}
          onChange={(e) => {
            const digits = e.target.value.replace(/\D/g, '');
            setForm((f) => ({ ...f, toleranciaAtrasoMin: digits ? parseInt(digits, 10) : 0 }));
          }}
          placeholder="0"
        />
      </label>
    </section>
  );
}

function TemplateSection({
  form,
  template,
  totalAulas,
  totalIntervalos,
  onAddSlot,
  onRemoveSlot,
  onUpdateSlot,
}: Pick<
  Props,
  'form' | 'template' | 'totalAulas' | 'totalIntervalos' | 'onAddSlot' | 'onRemoveSlot' | 'onUpdateSlot'
>) {
  return (
    <section className="ac-turno-section ac-turno-section--template">
      <div className="ac-turno-section__head ac-turno-section__head--template">
        <LayoutList size={14} />
        <div>
          <h3 className="ac-turno-section__title">Template do dia</h3>
          <span className="ac-turno-section__sub">
            {totalAulas} aula(s) · {totalIntervalos} intervalo(s) · {form.horaEntradaOficial}–{form.horaSaidaOficial}
          </span>
        </div>
        <div className="ac-turno-section__actions">
          <button type="button" className="ac-turno-btn ac-turno-btn--primary" onClick={() => onAddSlot('AULA')}>
            + Aula
          </button>
          <button type="button" className="ac-turno-btn" onClick={() => onAddSlot('INTERVALO')}>
            + Intervalo
          </button>
        </div>
      </div>
      <div className="ac-turno-slots">
        {template.map((slot, i) => (
          <div
            key={`${slot.numeroAula}-${i}`}
            className={`ac-turno-slot${slot.tipo === 'INTERVALO' ? ' ac-turno-slot--intervalo' : ''}`}
          >
            <span className={`ac-turno-slot__tipo ac-turno-slot__tipo--${slot.tipo.toLowerCase()}`}>
              {slot.tipo === 'AULA' ? 'Aula' : 'Int.'}
            </span>
            <input
              type="time"
              className="ac-turno-slot__time"
              value={slot.horaInicio}
              min={form.horaEntradaOficial}
              max={form.horaSaidaOficial}
              onChange={(e) => onUpdateSlot(i, 'horaInicio', e.target.value)}
            />
            <input
              type="time"
              className="ac-turno-slot__time"
              value={slot.horaFim}
              min={form.horaEntradaOficial}
              max={form.horaSaidaOficial}
              onChange={(e) => onUpdateSlot(i, 'horaFim', e.target.value)}
            />
            <input
              className="ac-turno-slot__desc"
              value={slot.descricao}
              onChange={(e) => onUpdateSlot(i, 'descricao', e.target.value)}
              placeholder="Nome"
              title={slot.descricao}
            />
            <button
              type="button"
              className="ac-turno-slot__remove"
              onClick={() => onRemoveSlot(i)}
              aria-label="Remover slot"
            >
              <X size={12} />
            </button>
          </div>
        ))}
        {template.length === 0 && (
          <p className="ac-turno-slots-empty">Nenhum slot. Use + Aula ou + Intervalo.</p>
        )}
      </div>
    </section>
  );
}

function FormBody(props: Props) {
  return (
    <div className="ac-turno-modal__form-grid">
      <PortoesSection {...props} />
      <TemplateSection {...props} />
    </div>
  );
}

export function TurnosFormModal(props: Props) {
  const { editing } = props;

  if (editing) {
    return (
      <div className="ac-modal-dual ac-turno-modal-dual">
        <HeroBlock {...props} />
        <div className="ac-modal-dual__columns">
          <div className="ac-modal-dual__main">
            <FormBody {...props} />
          </div>
          <div className="ac-modal-dual__divider" aria-hidden />
          <TurnoEditSidePanel
            turno={editing}
            totalAulas={props.totalAulas}
            totalIntervalos={props.totalIntervalos}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="ac-turno-modal-dual">
      <HeroBlock {...props} />
      <div className="ac-turno-modal__body ac-turno-modal__body--solo">
        <FormBody {...props} />
      </div>
    </div>
  );
}
